import { createContext, useContext, useEffect, useState } from 'react';

type ExtendedPhantomProvider = typeof window.solana & {
  on?: (event: string, handler: (...args: any[]) => void) => void;
  connect: (options?: { onlyIfTrusted?: boolean; appMetadata?: { name: string; icon: string } }) => Promise<any>;
};

interface WalletContextType {
  accountAddress: string | null;
  publicKey: any | null;
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  isConnected: boolean;
}

const WalletContext = createContext<WalletContextType>({
  accountAddress: null,
  publicKey: null,
  connect: async () => {},
  disconnect: async () => {},
  isConnected: false,
});

export const useWallet = () => useContext(WalletContext);

export const WalletProvider = ({ children }: { children: React.ReactNode }) => {
  const [accountAddress, setAccountAddress] = useState<string | null>(null);
  const [publicKey, setPublicKey] = useState<any | null>(null);

  const getPhantomProvider = (): ExtendedPhantomProvider | null => {
    if ('solana' in window) {
      const provider = window.solana as ExtendedPhantomProvider;
      if (provider?.isPhantom) return provider;
    }
    return null;
  };

  const connect = async () => {
    const provider = getPhantomProvider();
    if (!provider) {
      alert('Phantom Wallet not found. Please install it.');
      return;
    }

    try {
      const resp = await provider.connect({
        onlyIfTrusted: false,
        appMetadata: {
          name: "SolMiner 1.0.3", // ✅ Your dApp name
          icon: `${window.location.origin}/logo.png`, // ✅ Uses logo from public folder
        },
      });

      setPublicKey(resp.publicKey);
      setAccountAddress(resp.publicKey.toString());
    } catch (err) {
      console.error('Connection failed:', err);
    }
  };

  const disconnect = async () => {
    const provider = getPhantomProvider();
    try {
      await provider?.disconnect?.();
      setPublicKey(null);
      setAccountAddress(null);
    } catch (err) {
      console.error('Disconnection failed:', err);
    }
  };

  useEffect(() => {
    const provider = getPhantomProvider();

    provider?.on?.('disconnect', () => {
      setPublicKey(null);
      setAccountAddress(null);
    });
  }, []);

  return (
    <WalletContext.Provider
      value={{
        accountAddress,
        publicKey,
        connect,
        disconnect,
        isConnected: !!accountAddress,
      }}
    >
      {children}
    </WalletContext.Provider>
  );
};
